import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AddProductsToImportResponse,
  CatalogImport,
  ProductItem,
  SubmitCatalogImportApplicationErrors,
  SubmitCatalogImportResponse,
  addProductsToImport as universalAddProductsToImport,
  createCatalogImport as universalCreateCatalogImport,
  submitCatalogImport as universalSubmitCatalogImport,
} from './stores-catalog-v3-catalog-import-catalog-imports-v-3.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/stores' };

export function createCatalogImport(
  httpClient: HttpClient
): CreateCatalogImportSignature {
  return (catalogImport: CatalogImport) =>
    universalCreateCatalogImport(
      catalogImport,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateCatalogImportSignature {
  /**
   * Creates a CatalogImport for upsert product operations.
   *
   * Returns `catalogImport.id` that can be used in subsequent AddProductsToImport and SubmitCatalogImport calls.
   * @param - CatalogImport to be created.
   * @returns Created CatalogImport.
   */
  (catalogImport: CatalogImport): Promise<CatalogImport>;
}

export function addProductsToImport(
  httpClient: HttpClient
): AddProductsToImportSignature {
  return (
    catalogImportId: string,
    products: NonNullablePaths<ProductItem, `product` | `product.handle`, 1>[]
  ) =>
    universalAddProductsToImport(
      catalogImportId,
      products,
      // @ts-ignore
      { httpClient }
    );
}

interface AddProductsToImportSignature {
  /**
   * Add products in bulk to an existing catalog import session.
   *
   * Products are stored in temporary storage until SubmitCatalogImport is called.
   * Can be called multiple times for the same import session.
   *
   * > **Notes:**
   * > + Products are upserted based on the `handle` field
   * > + Updates don't require passing the current revision number and overwrites existing fields which are specified in `product`
   * > + Pass `category_ids` to update categories assignments, overwrites existing assignments
   * @param - Catalog Import ID.
   * @param - List of product items to add to the import session.
   */
  (
    catalogImportId: string,
    products: NonNullablePaths<ProductItem, `product` | `product.handle`, 1>[]
  ): Promise<
    NonNullablePaths<
      AddProductsToImportResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      4
    >
  >;
}

export function submitCatalogImport(
  httpClient: HttpClient
): SubmitCatalogImportSignature {
  return (catalogImportId: string) =>
    universalSubmitCatalogImport(
      catalogImportId,
      // @ts-ignore
      { httpClient }
    );
}

interface SubmitCatalogImportSignature {
  /**
   * Submits all products in the catalog import for processing.
   * Once called, no more items can be added to this import session.
   * @param - Catalog Import ID.
   */
  (catalogImportId: string): Promise<
    NonNullablePaths<
      SubmitCatalogImportResponse,
      `jobId` | `totalProductsCount`,
      0
    > & {
      __applicationErrorsType?: SubmitCatalogImportApplicationErrors;
    }
  >;
}

export {
  AccountInfo,
  ActionEvent,
  AddProductResult,
  AddProductsToImportRequest,
  AddProductsToImportResponse,
  Alignment,
  AnchorData,
  Animation,
  AppEmbedData,
  AppEmbedDataAppDataOneOf,
  AppType,
  ApplicationError,
  AspectRatio,
  AudioData,
  Backdrop,
  BackdropType,
  Background,
  BackgroundGradient,
  BackgroundImage,
  BackgroundType,
  Banner,
  BannerPosition,
  BlockquoteData,
  BookingData,
  Border,
  BorderColors,
  BorderWidths,
  Brand,
  BulkActionMetadata,
  BulletedListData,
  ButtonData,
  ButtonDataType,
  ButtonStyles,
  CaptionData,
  CardData,
  CardDataBackground,
  CardDataBackgroundType,
  CardStyles,
  CardStylesType,
  CatalogImport,
  CatalogImportAlreadySubmittedErrorData,
  CellStyle,
  CheckboxListData,
  ChoiceType,
  ChoicesSettings,
  CodeBlockData,
  CollapsibleListData,
  ColorData,
  Colors,
  ColumnSize,
  ConnectedModifier,
  ConnectedModifierChoice,
  ConnectedModifierChoiceValueOneOf,
  ConnectedModifierModifierSettingsOneOf,
  ConnectedOption,
  ConnectedOptionChoice,
  ConnectedOptionChoiceValueOneOf,
  ConnectedOptionOptionSettingsOneOf,
  CreateCatalogImportRequest,
  CreateCatalogImportResponse,
  Crop,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  Decoration,
  DecorationDataOneOf,
  DecorationType,
  Design,
  DesignTarget,
  Dimensions,
  DimensionsUnit,
  Direction,
  DiscountType,
  DividerData,
  DividerDataAlignment,
  DividerDataStyles,
  DocumentStyle,
  DomainEvent,
  DomainEventBodyOneOf,
  EmbedData,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EntranceAnimation,
  EntranceEffect,
  EntranceEffectType,
  EventData,
  ExtendedFields,
  FileData,
  FileSource,
  FileSourceDataOneOf,
  FileType,
  FixedMonetaryAmount,
  FontFamilyData,
  FontSizeData,
  FontType,
  FreeTextSettings,
  GIF,
  GIFData,
  GIFType,
  GalleryData,
  GalleryOptions,
  GalleryOptionsLayout,
  Gradient,
  GradientType,
  HTMLData,
  HTMLDataDataOneOf,
  HeadingData,
  Height,
  IdentificationData,
  IdentificationDataIdOneOf,
  Image,
  ImageData,
  ImageDataCrop,
  ImageDataStyles,
  ImagePosition,
  ImagePositionPosition,
  ImageScalingScaling,
  ImageStyles,
  Indentation,
  InfoSection,
  InitialExpandedItems,
  InventoryItemComposite,
  InventoryItemCompositeTrackingMethodOneOf,
  Item,
  ItemDataOneOf,
  ItemMetadata,
  ItemStyle,
  Keyword,
  LayerEffect,
  Layers,
  Layout,
  LayoutCellData,
  LayoutData,
  LayoutDataBackground,
  LayoutDataBackgroundImage,
  LayoutDataBackgroundType,
  LayoutType,
  LineCap,
  LineStyle,
  Link,
  LinkData,
  LinkDataOneOf,
  LinkPreviewData,
  LinkPreviewDataStyles,
  ListItemNodeData,
  ListStyle,
  ListValue,
  LoopAnimation,
  LoopEffect,
  LoopEffectType,
  MapData,
  MapSettings,
  MapType,
  MeasurementUnit,
  Media,
  MediaItemsInfo,
  MediaReference,
  MediaReferenceIdentifierOneOf,
  MediaReferences,
  MediaType,
  MentionData,
  MessageEnvelope,
  Metadata,
  ModifierChoicesSettings,
  ModifierRenderType,
  MultipleColors,
  Node,
  NodeDataOneOf,
  NodeStyle,
  NodeType,
  NullValue,
  Oembed,
  Option,
  OptionChoiceIds,
  OptionChoiceNames,
  OptionChoiceReferences,
  OptionDesign,
  OptionLayout,
  OrderedListData,
  Orientation,
  Origin,
  PDFSettings,
  ParagraphData,
  Permissions,
  PhysicalProperties,
  Placement,
  PlaybackOptions,
  PluginContainerData,
  PluginContainerDataAlignment,
  PluginContainerDataWidth,
  PluginContainerDataWidthDataOneOf,
  PointerEffect,
  PointerEffectType,
  Poll,
  PollData,
  PollDataLayout,
  PollDesign,
  PollDesignBackground,
  PollDesignBackgroundBackgroundOneOf,
  PollDesignBackgroundType,
  PollLayout,
  PollLayoutDirection,
  PollLayoutType,
  PollSettings,
  Position,
  PreorderInfo,
  PriceInfo,
  PricePerUnit,
  PricePerUnitRange,
  PricePerUnitRangePricePerUnit,
  PricePerUnitSettings,
  PricingData,
  ProductCategoryIdsInfo,
  ProductItem,
  ProductMedia,
  ProductMediaMediaOneOf,
  ProductMediaSetByOneOf,
  ProductOptionRenderType,
  ProductType,
  ProductWithInventory,
  ProductWithInventoryTypedPropertiesOneOf,
  PurchaseEligibility,
  QueryProductItemsInImportRequest,
  QueryProductItemsInImportResponse,
  Rel,
  Resizing,
  ResponsivenessBehaviour,
  RestoreInfo,
  RevenueDetails,
  Ribbon,
  RibbonStyles,
  RichContent,
  Scaling,
  SectionDivider,
  SecuredMedia,
  SeoSchema,
  Settings,
  Shape,
  ShapeData,
  ShapeDataStyles,
  SketchData,
  SmartBlockCellData,
  SmartBlockData,
  SmartBlockDataType,
  SortOrder,
  Sorting,
  Source,
  Spoiler,
  SpoilerData,
  Stop,
  Styles,
  StylesBorder,
  StylesPosition,
  SubmitCatalogImportRequest,
  SubmitCatalogImportResponse,
  Subscription,
  SubscriptionCyclesOneOf,
  SubscriptionDetails,
  SubscriptionDiscount,
  SubscriptionDiscountDiscountOneOf,
  SubscriptionFrequency,
  TableCellData,
  TableData,
  TableDataDimensions,
  Tag,
  TagList,
  Tags,
  Target,
  TextAlignment,
  TextData,
  TextNodeStyle,
  TextStyle,
  Thumbnail,
  Thumbnails,
  ThumbnailsAlignment,
  TocData,
  Type,
  UserCriteria,
  V1Media,
  Variant,
  VariantDigitalProperties,
  VariantPhysicalProperties,
  VariantWithInventory,
  VariantWithInventoryTypedPropertiesOneOf,
  VariantsInfo,
  VerticalAlignment,
  VerticalAlignmentAlignment,
  Video,
  VideoData,
  VideoResolution,
  ViewMode,
  ViewRole,
  VoteRole,
  WebhookIdentityType,
  WeightMeasurementUnitInfo,
  WeightRange,
  WeightUnit,
  Width,
  WidthType,
} from './stores-catalog-v3-catalog-import-catalog-imports-v-3.universal.js';
